import { algoliasearch } from "algoliasearch";
import type { SearchClient } from "algoliasearch";
import axiosInstance from "@/api/axiosInstance";

export interface SearchConfig {
  securedApiKey: string;
  eventsSecuredApiKey?: string | null;
  /**
   * Secured key scoped to the events index AND the user's favorited
   * event ids. Generated server-side. `favoritesSecuredApiKey` is the
   * resource counterpart — applying that key to the events index
   * returns zero hits because its filter is `(resourceId:X OR ...)`,
   * which can't match event records.
   */
  eventsFavoritesSecuredApiKey?: string | null;
  favoritesSecuredApiKey?: string | null;
  indices: {
    events: string;
    /**
     * Algolia virtual replica of `events`, ranked by `asc(nextSessionStartDate)`.
     * The public events directory grid queries this replica so events with the
     * soonest upcoming session appear first. Settings live in the Algolia
     * dashboard; the replica's records mirror the primary in real time.
     */
    eventsByNextSessionAsc?: string;
    /**
     * Algolia standard replica of `events`, ranked by `asc(eventName)`. The
     * public events directory grid queries this replica so events sort
     * alphabetically A→Z (the current default). Settings live in the Algolia
     * dashboard; the replica's records mirror the primary in real time.
     */
    eventsByNameAsc?: string;
    resources: string;
  };
  applicationId: string;
  userTradeIds: number[];
  userTradeNames: string[];
  isCertainPath: boolean;
  expiresAt: number;
}

export interface SearchResult {
  objectID: string;
  eventId?: number;
  resourceId?: number;
  uuid?: string;
  slug?: string;
  title?: string;
  eventName?: string;
  eventDescription?: string;
  searchDescription?: string;
  eventPrice?: number;
  eventType?: string;
  eventCategory?: string;
  eventCategories?: string[];
  /** Unix seconds of the event's next published session, or null. */
  nextSessionStartDate?: number | null;
  resourceType?: string;
  tags?: string[];
  categories?: string[];
  trades?: string[];
  url: string;
  thumbnail?: string;
  primaryIcon?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  viewCount?: number;
  sessions?: Array<{
    name: string;
    startDate: string;
    endDate: string;
    venue?: string;
    location?: string;
    isVirtual: boolean;
    instructor?: string;
  }>;
  _highlightResult?: any;
}

export interface SearchFilters {
  type?: "all" | "events" | "resources";
  resourceType?: string;
  eventType?: string;
  eventTypes?: string[];
  eventCategory?: string;
  eventCategories?: string[];
  tags?: string[];
  categories?: string[];
  businessRoles?: string[];
  trades?: string[];
  processWheel?: string[];
  dateRange?: {
    start?: Date;
    end?: Date;
  };
  numericFilters?: string[];
  isEligibleForLata?: boolean;
}

export interface FacetValue {
  value: string;
  count: number;
  isRefined?: boolean;
}

export interface SearchFacets {
  resourceType?: Record<string, number>;
  eventType?: Record<string, number>;
  eventCategory?: Record<string, number>;
  eventCategories?: Record<string, number>;
  tags?: Record<string, number>;
  categories?: Record<string, number>;
  businessRoles?: Record<string, number>;
  trades?: Record<string, number>;
  processWheel?: Record<string, number>;
}

class SearchService {
  private searchClient: SearchClient | null = null;
  /**
   * Client on the events-only secured key. That key carries the company's
   * event visibility rules (published + stage clause) — the resources key
   * must never be used against the events index.
   */
  private eventsSearchClient: SearchClient | null = null;
  private config: SearchConfig | null = null;
  private configPromise: Promise<SearchConfig> | null = null;
  private favoritesKey: string | null = null;
  private eventsFavoritesKey: string | null = null;
  private favoriteUuids: string[] = [];
  private sharedWithMeKey: string | null = null;
  private sharedWithMeUuids: string[] = [];
  private currentContext:
    | "library"
    | "library-v2"
    | "podcasts"
    | "news"
    | "partner-network"
    | null = null;

  /**
   * Read-only accessor for the resolved index names. Returns null until
   * `initialize()` has fetched the search config from the API.
   */
  getIndices(): SearchConfig["indices"] | null {
    return this.config?.indices ?? null;
  }

  /**
   * Initialize the search service with secured configuration from the API
   * @param forceRefresh - Force refresh the configuration from the server
   * @param context - 'library' (exclude Podcast/News/Partner Page), 'library-v2' (library with company-stage visibility gating), 'podcasts' (only Podcast), 'news' (only News), 'partner-network' (only Partner Page), or null (all resources)
   */
  async initialize(
    forceRefresh: boolean = false,
    context?:
      | "library"
      | "library-v2"
      | "podcasts"
      | "news"
      | "partner-network",
  ): Promise<void> {
    // If context changed, force refresh. No context (the global search
    // modal) is itself a context — switching between it and a page context
    // (e.g. "library-v2") must invalidate the cached config, otherwise the
    // page reuses the modal's key (or vice versa) and shows the wrong
    // result set.
    const nextContext = context ?? null;
    if (nextContext !== this.currentContext) {
      forceRefresh = true;
      this.currentContext = nextContext;
      this.config = null;
      this.searchClient = null;
      this.eventsSearchClient = null;
    }

    // If we already have config and it's not a force refresh, skip re-initialization
    if (!forceRefresh && this.config && this.searchClient) {
      return;
    }

    if (forceRefresh || !this.configPromise) {
      // Force a refresh by calling the refresh endpoint
      if (forceRefresh) {
        this.configPromise = this.refreshConfig(context);
      } else {
        this.configPromise = this.fetchConfig(context);
      }
    }

    this.config = await this.configPromise;

    // Store favorites key separately if present (for backward compatibility)
    // Note: We don't override the favoritesKey here if it's already set by updateFavoritesKey
    if (this.config.favoritesSecuredApiKey && !this.favoritesKey) {
      this.favoritesKey = this.config.favoritesSecuredApiKey;
    }
    // Events favorites use a separate key — the resources favorites
    // key filters on `resourceId` which doesn't exist on event records.
    if (this.config.eventsFavoritesSecuredApiKey) {
      this.eventsFavoritesKey = this.config.eventsFavoritesSecuredApiKey;
    }

    // Initialize Algolia client with secured key
    this.searchClient = algoliasearch(
      this.config.applicationId,
      this.config.securedApiKey,
    );
    this.eventsSearchClient = this.createEventsClient(this.config);
  }

  private createEventsClient(config: SearchConfig): SearchClient | null {
    return config.eventsSecuredApiKey
      ? algoliasearch(config.applicationId, config.eventsSecuredApiKey)
      : null;
  }

  /**
   * Update only the favorites secured key
   * This allows refreshing favorites without regenerating base security
   */
  updateFavoritesKey(
    favoritesKey: string | null,
    favoriteUuids: string[],
  ): void {
    this.favoritesKey = favoritesKey;
    this.favoriteUuids = favoriteUuids;
  }

  /**
   * Get the current favorite resource UUIDs
   */
  getFavoriteUuids(): string[] {
    return this.favoriteUuids;
  }

  /**
   * Update only the shared-with-me secured key
   * This allows refreshing shared resources without regenerating base security
   */
  updateSharedWithMeKey(
    sharedWithMeKey: string | null,
    sharedWithMeUuids: string[],
  ): void {
    this.sharedWithMeKey = sharedWithMeKey;
    this.sharedWithMeUuids = sharedWithMeUuids;
  }

  /**
   * Get the current shared-with-me resource UUIDs
   */
  getSharedWithMeUuids(): string[] {
    return this.sharedWithMeUuids;
  }

  /**
   * Force refresh the configuration from the API
   */
  private async refreshConfig(
    context?:
      | "library"
      | "library-v2"
      | "podcasts"
      | "news"
      | "partner-network",
  ): Promise<SearchConfig> {
    // Refresh endpoint doesn't support context yet, so use the appropriate config endpoint
    const endpoint =
      context === "library"
        ? "/api/search/config/library"
        : context === "library-v2"
          ? "/api/search/config/library-v2"
          : context === "podcasts"
            ? "/api/search/config/podcasts"
            : context === "news"
              ? "/api/search/config/news"
              : context === "partner-network"
                ? "/api/search/config/partner-network"
                : "/api/search/config";

    const response = await axiosInstance.get<SearchConfig>(endpoint);
    return response.data;
  }

  /**
   * Fetch secured search configuration from the API
   */
  private async fetchConfig(
    context?:
      | "library"
      | "library-v2"
      | "podcasts"
      | "news"
      | "partner-network",
  ): Promise<SearchConfig> {
    const endpoint =
      context === "library"
        ? "/api/search/config/library"
        : context === "library-v2"
          ? "/api/search/config/library-v2"
          : context === "podcasts"
            ? "/api/search/config/podcasts"
            : context === "news"
              ? "/api/search/config/news"
              : context === "partner-network"
                ? "/api/search/config/partner-network"
                : "/api/search/config";

    const response = await axiosInstance.get<SearchConfig>(endpoint);
    return response.data;
  }

  /**
   * Refresh the search configuration if expired
   */
  async refreshIfNeeded(): Promise<void> {
    if (!this.config) {
      await this.initialize();
      return;
    }

    // Check if the key is about to expire (within 5 minutes)
    const now = Math.floor(Date.now() / 1000);
    if (now >= this.config.expiresAt - 300) {
      // Re-fetch through the context-aware path — the plain refresh endpoint
      // returns the no-context (global search) config, which would silently
      // swap a page context's key for the modal's.
      this.config = await this.refreshConfig(this.currentContext ?? undefined);

      // Reinitialize client with new key
      this.searchClient = algoliasearch(
        this.config.applicationId,
        this.config.securedApiKey,
      );
      this.eventsSearchClient = this.createEventsClient(this.config);
    }
  }

  /**
   * Ensure the service is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.searchClient || !this.config) {
      await this.initialize();
    } else {
      await this.refreshIfNeeded();
    }
  }

  /**
   * Search across all indices
   */
  async searchAll(
    query: string,
    filters: SearchFilters = {},
    page: number = 0,
    hitsPerPage: number = 20,
  ): Promise<{
    events: SearchResult[];
    resources: SearchResult[];
    totalEvents: number;
    totalResources: number;
    facets: SearchFacets;
  }> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error("Search service not initialized");
    }

    // A filter that only exists on one index rules the other out: picking a
    // resource type means "show me resources", an event type "show me events".
    const hasResourceOnlyFilter =
      !!filters.resourceType ||
      (filters.categories?.length ?? 0) > 0 ||
      (filters.processWheel?.length ?? 0) > 0;
    const hasEventOnlyFilter =
      !!filters.eventType ||
      !!filters.eventCategory ||
      (filters.eventTypes?.length ?? 0) > 0 ||
      (filters.eventCategories?.length ?? 0) > 0;
    const type = filters.type ?? "all";
    const wantResources =
      (type === "all" || type === "resources") && !hasEventOnlyFilter;
    const wantEvents =
      (type === "all" || type === "events") &&
      !hasResourceOnlyFilter &&
      this.eventsSearchClient !== null;

    const queries = [];

    if (wantResources) {
      const resourceFilters = this.buildResourceFilters(filters);
      queries.push({
        indexName: this.config.indices.resources,
        query,
        page,
        hitsPerPage,
        // Request facets to get dynamic filter options
        facets: [
          "resourceType",
          "tags",
          "categories",
          "businessRoles",
          "trades",
        ],
        // Only add filters if there are any
        ...(resourceFilters ? { filters: resourceFilters } : {}),
      });
    }

    // An all-digits query doubles as an exact resource-ID lookup (#9243):
    // batch a side query filtered on resourceId alongside the text search
    // (Algolia can't OR a numeric filter into a text query).
    const idQuery = query.trim();
    const isIdLookup = wantResources && /^\d+$/.test(idQuery);
    if (isIdLookup) {
      const resourceFilters = this.buildResourceFilters(filters);
      queries.push({
        indexName: this.config.indices.resources,
        query: "",
        page: 0,
        hitsPerPage: 1,
        filters: resourceFilters
          ? `(${resourceFilters}) AND resourceId=${idQuery}`
          : `resourceId=${idQuery}`,
      });
    }

    // Events run on their own client (the events key), in parallel.
    const eventFilters = this.buildEventFilters(filters);
    const [results, eventResults] = await Promise.all([
      queries.length > 0
        ? this.searchClient.search(queries)
        : Promise.resolve({ results: [] }),
      wantEvents && this.eventsSearchClient
        ? this.eventsSearchClient.searchSingleIndex({
            indexName: this.config.indices.events,
            searchParams: {
              query,
              page,
              hitsPerPage,
              facets: ["eventType", "eventCategories"],
              ...(eventFilters ? { filters: eventFilters } : {}),
            },
          })
        : Promise.resolve({ hits: [], nbHits: 0, facets: {} }),
    ]);

    // Parse results based on what was searched
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let resourceResults: any = { hits: [], nbHits: 0, facets: {} };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let idResults: any = { hits: [] };

    // The id side query targets the same resources index as the text query,
    // so peel it off positionally (it was pushed last).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawResults = [...(results.results as any[])];
    if (isIdLookup) {
      idResults = rawResults.pop() ?? { hits: [] };
    }
    resourceResults = rawResults[0] ?? resourceResults;

    // Combine facets from both indices
    const combinedFacets: SearchFacets = {};

    const eventFacets: Record<
      string,
      Record<string, number>
    > = eventResults.facets ?? {};
    if (eventFacets.eventType) {
      combinedFacets.eventType = eventFacets.eventType;
    }
    if (eventFacets.eventCategories) {
      combinedFacets.eventCategories = eventFacets.eventCategories;
    }

    if (resourceResults.facets) {
      if (resourceResults.facets.resourceType) {
        combinedFacets.resourceType = resourceResults.facets.resourceType;
      }
      if (resourceResults.facets.categories) {
        combinedFacets.categories = resourceResults.facets.categories;
      }
      if (resourceResults.facets.tags) {
        // Merge tags from resources
        combinedFacets.tags = {
          ...(combinedFacets.tags || {}),
          ...resourceResults.facets.tags,
        };
      }
      if (resourceResults.facets.businessRoles) {
        combinedFacets.businessRoles = resourceResults.facets.businessRoles;
      }
      if (resourceResults.facets.trades) {
        combinedFacets.trades = resourceResults.facets.trades;
      }
    }

    // Surface the exact-ID match first, deduped against the text hits.
    const idHits = (idResults.hits ?? []) as SearchResult[];
    const textHits = resourceResults.hits as SearchResult[];
    const mergedResourceHits =
      idHits.length > 0
        ? [
            ...idHits,
            ...textHits.filter(
              (hit) => !idHits.some((idHit) => idHit.uuid === hit.uuid),
            ),
          ]
        : textHits;

    return {
      events: eventResults.hits as SearchResult[],
      resources: mergedResourceHits,
      totalEvents: eventResults.nbHits ?? 0,
      totalResources:
        idHits.length > 0
          ? mergedResourceHits.length > resourceResults.nbHits
            ? resourceResults.nbHits + 1
            : resourceResults.nbHits
          : resourceResults.nbHits,
      facets: combinedFacets,
    };
  }

  /**
   * Search only events.
   *
   * @param query
   * @param filters
   * @param page
   * @param hitsPerPage
   * @param useFavoritesKey
   * @param indexNameOverride
   *
   * When set, query this index name instead of the
   * primary `indices.events`. Used by the public events directory to target
   * the `eventsByNextSessionAsc` replica so the grid sorts by the next-upcoming-session date.
   */
  async searchEvents(
    query: string,
    filters: SearchFilters = {},
    page: number = 0,
    hitsPerPage: number = 20,
    useFavoritesKey: boolean = false,
    indexNameOverride?: string,
  ): Promise<{ hits: SearchResult[]; total: number; facets: SearchFacets }> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error("Search service not initialized");
    }

    // Use the events-specific secured key (no resource-specific filters baked in)
    const eventsKey =
      this.config.eventsSecuredApiKey || this.config.securedApiKey;
    let clientToUse = algoliasearch(this.config.applicationId, eventsKey);

    if (useFavoritesKey && this.eventsFavoritesKey) {
      // Use the events-specific favorites key. The resource favorites
      // key (`this.favoritesKey`) filters on `resourceId`, which event
      // records don't expose — applying it to the events index returns
      // zero hits, which is the bug we're fixing here.
      clientToUse = algoliasearch(
        this.config.applicationId,
        this.eventsFavoritesKey,
      );
    } else if (useFavoritesKey && !this.eventsFavoritesKey) {
      // User has no event favorites — short-circuit to an empty result.
      return { hits: [], total: 0, facets: {} };
    }

    const searchParams: any = {
      query,
      page,
      hitsPerPage,
      filters: this.buildEventFilters(filters),
      facets: [
        "eventType",
        "eventCategories",
        "trades",
        "businessRoles",
        "tags",
      ],
    };

    if (filters.numericFilters && filters.numericFilters.length > 0) {
      searchParams.numericFilters = filters.numericFilters;
    }

    const result = await clientToUse.searchSingleIndex({
      indexName: indexNameOverride ?? this.config.indices.events,
      searchParams,
    });

    const facets: SearchFacets = {};
    if (result.facets) {
      if (result.facets.eventType) {
        facets.eventType = result.facets.eventType;
      }
      if (result.facets.eventCategories) {
        facets.eventCategories = result.facets.eventCategories;
      }
      if (result.facets.trades) {
        facets.trades = result.facets.trades;
      }
      if (result.facets.businessRoles) {
        facets.businessRoles = result.facets.businessRoles;
      }
      if (result.facets.tags) {
        facets.tags = result.facets.tags;
      }
    }

    return {
      hits: result.hits as SearchResult[],
      total: result.nbHits || 0,
      facets,
    };
  }

  /**
   * Get the appropriate resource index based on sort option
   * Dynamically constructs replica index names based on the base index
   */
  private getResourceIndexForSort(sortOption?: string): string {
    if (!this.config) {
      throw new Error("Search config not initialized");
    }

    // If no sort option or default, use the primary index
    if (!sortOption || sortOption === "newest") {
      // Default to newest (publishStartDate desc) replica
      return `${this.config.indices.resources}_publishStartDate_desc`;
    }

    // Map sort options to replica index suffixes
    const replicaSuffixes: Record<string, string> = {
      oldest: "_publishStartDate_asc",
      mostViewed: "_viewCount_desc",
      alphabetical: "_title_asc",
    };

    const suffix = replicaSuffixes[sortOption];
    if (!suffix) {
      // Fallback to default replica if unknown sort option
      console.warn(
        `Unknown sort option: ${sortOption}, falling back to newest`,
      );
      return `${this.config.indices.resources}_publishStartDate_desc`;
    }

    return this.config.indices.resources + suffix;
  }

  /**
   * Search only resources
   */
  async searchResources(
    query: string,
    filters: SearchFilters = {},
    page: number = 0,
    hitsPerPage: number = 20,
    sortOption?: string,
    useFavoritesKey: boolean = false,
    useSharedWithMeKey: boolean = false,
  ): Promise<{ hits: SearchResult[]; total: number; facets: SearchFacets }> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error("Search service not initialized");
    }

    // Determine which client/key to use
    let clientToUse = this.searchClient;

    // Shared-with-me key takes precedence if requested
    if (useSharedWithMeKey && this.sharedWithMeKey) {
      clientToUse = algoliasearch(
        this.config.applicationId,
        this.sharedWithMeKey,
      );
    } else if (useSharedWithMeKey && !this.sharedWithMeKey) {
      // Return empty results if shared-with-me requested but user has none
      return {
        hits: [],
        total: 0,
        facets: {},
      };
    } else if (useFavoritesKey && this.favoritesKey) {
      // Use favorites secured key if requested and available
      clientToUse = algoliasearch(this.config.applicationId, this.favoritesKey);
    } else if (useFavoritesKey && !this.favoritesKey) {
      // Return empty results if favorites requested but user has none
      return {
        hits: [],
        total: 0,
        facets: {},
      };
    }

    // Get the appropriate index based on sort option
    const indexName = this.getResourceIndexForSort(sortOption);

    // Build search params for Algolia
    const searchParams: any = {
      query,
      page,
      hitsPerPage,
      filters: this.buildResourceFilters(filters),
      facets: [
        "resourceType",
        "tags",
        "categories",
        "businessRoles",
        "trades",
        "processWheel",
      ],
    };

    const result = await clientToUse.searchSingleIndex({
      indexName,
      searchParams,
    });

    // An all-digits query doubles as an exact resource-ID lookup (#9243):
    // run a side search filtered on resourceId and surface the exact match
    // first (Algolia can't OR a numeric filter into a text query).
    let idHits: SearchResult[] = [];
    const idQuery = query.trim();
    if (/^\d+$/.test(idQuery)) {
      try {
        const baseFilters = this.buildResourceFilters(filters);
        const idResult = await clientToUse.searchSingleIndex({
          indexName,
          searchParams: {
            query: "",
            page: 0,
            hitsPerPage: 1,
            filters: baseFilters
              ? `(${baseFilters}) AND resourceId=${idQuery}`
              : `resourceId=${idQuery}`,
          } as any,
        });
        idHits = idResult.hits as SearchResult[];
      } catch {
        // Exact-id lookup is best-effort; the text search stands alone.
      }
    }

    // Extract facets from result
    const facets: SearchFacets = {};
    if (result.facets) {
      if (result.facets.resourceType) {
        facets.resourceType = result.facets.resourceType;
      }
      if (result.facets.categories) {
        facets.categories = result.facets.categories;
      }
      if (result.facets.tags) {
        facets.tags = result.facets.tags;
      }
      if (result.facets.businessRoles) {
        facets.businessRoles = result.facets.businessRoles;
      }
      if (result.facets.trades) {
        facets.trades = result.facets.trades;
      }
      if (result.facets.processWheel) {
        facets.processWheel = result.facets.processWheel;
      }
    }

    const textHits = result.hits as SearchResult[];
    const mergedHits =
      idHits.length > 0
        ? [
            ...idHits,
            ...textHits.filter(
              (hit) => !idHits.some((idHit) => idHit.uuid === hit.uuid),
            ),
          ]
        : textHits;

    return {
      hits: mergedHits,
      total: result.nbHits || 0,
      facets,
    };
  }

  /**
   * Get search suggestions for autocomplete
   */
  async getSuggestions(
    query: string,
    limit: number = 5,
  ): Promise<SearchResult[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const results = await this.searchAll(query, {}, 0, limit);

    // Up to half events, the rest resources (all resources when no events match).
    const events = results.events.slice(0, Math.floor(limit / 2));
    return [...events, ...results.resources.slice(0, limit - events.length)];
  }

  /**
   * Build filter string for events
   */
  private buildEventFilters(filters: SearchFilters): string {
    const filterParts: string[] = [];

    if (filters.eventTypes && filters.eventTypes.length > 0) {
      const typeFilters = filters.eventTypes
        .map((t) => `eventType:"${t}"`)
        .join(" OR ");
      filterParts.push(`(${typeFilters})`);
    } else if (filters.eventType) {
      filterParts.push(`eventType:"${filters.eventType}"`);
    }

    if (filters.eventCategories && filters.eventCategories.length > 0) {
      const catFilters = filters.eventCategories
        .map((c) => `eventCategories:"${c}"`)
        .join(" OR ");
      filterParts.push(`(${catFilters})`);
    } else if (filters.eventCategory) {
      filterParts.push(`eventCategories:"${filters.eventCategory}"`);
    }

    if (filters.trades && filters.trades.length > 0) {
      const tradeFilters = filters.trades
        .map((t) => `trades:"${t}"`)
        .join(" OR ");
      filterParts.push(`(${tradeFilters})`);
    }

    if (filters.businessRoles && filters.businessRoles.length > 0) {
      const roleFilters = filters.businessRoles
        .map((r) => `businessRoles:"${r}"`)
        .join(" OR ");
      filterParts.push(`(${roleFilters})`);
    }

    if (filters.tags && filters.tags.length > 0) {
      const tagFilters = filters.tags
        .map((tag) => `tags:"${tag}"`)
        .join(" OR ");
      filterParts.push(`(${tagFilters})`);
    }

    if (filters.isEligibleForLata) {
      filterParts.push("isEligibleForLata:true");
    }

    return filterParts.join(" AND ");
  }

  /**
   * Build filter string for resources
   */
  private buildResourceFilters(filters: SearchFilters): string {
    const filterParts: string[] = [];

    // Drop resources whose publishEndDate has passed. Resources that never
    // expire are indexed with Resource::NEVER_EXPIRES_TIMESTAMP (year 3000) so
    // the same numeric filter matches them. See the constant's docblock for why
    // a sentinel is used instead of a boolean flag.
    const nowSec = Math.floor(Date.now() / 1000);
    filterParts.push(`publishEndDate >= ${nowSec}`);

    if (filters.resourceType) {
      filterParts.push(`resourceType:"${filters.resourceType}"`);
    }

    if (filters.categories && filters.categories.length > 0) {
      const categoryFilters = filters.categories
        .map((cat) => `categories:"${cat}"`)
        .join(" OR ");
      filterParts.push(`(${categoryFilters})`);
    }

    if (filters.tags && filters.tags.length > 0) {
      const tagFilters = filters.tags
        .map((tag) => `tags:"${tag}"`)
        .join(" OR ");
      filterParts.push(`(${tagFilters})`);
    }

    if (filters.businessRoles && filters.businessRoles.length > 0) {
      const roleFilters = filters.businessRoles
        .map((role) => `businessRoles:"${role}"`)
        .join(" OR ");
      filterParts.push(`(${roleFilters})`);
    }

    if (filters.trades && filters.trades.length > 0) {
      const tradeFilters = filters.trades
        .map((trade) => `trades:"${trade}"`)
        .join(" OR ");
      filterParts.push(`(${tradeFilters})`);
    }

    if (filters.processWheel && filters.processWheel.length > 0) {
      const processWheelFilters = filters.processWheel
        .map((pw) => `processWheel:"${pw}"`)
        .join(" OR ");
      filterParts.push(`(${processWheelFilters})`);
    }

    return filterParts.join(" AND ");
  }

  /**
   * Get the current user's trade IDs (for display purposes)
   */
  getUserTradeIds(): number[] {
    return this.config?.userTradeIds || [];
  }

  /**
   * Get the current user's trade names (used to scope the trade filter sidebar
   * since Algolia's multivalued facets leak trades from co-tagged resources).
   */
  getUserTradeNames(): string[] {
    return this.config?.userTradeNames || [];
  }

  /**
   * Whether the current company is CertainPath (sees all trades).
   */
  isCertainPath(): boolean {
    return this.config?.isCertainPath ?? false;
  }
}

// Export singleton instance
export const searchService = new SearchService();
